import React, { useState, useEffect, useRef } from "react";

function get(obj, path, fallback) {
  var result = obj;
  var parts = path.split(".");
  for (var i = 0; i < parts.length; i++) {
    if (result === null || result === undefined) return fallback;
    result = result[parts[i]];
  }
  return result === undefined || result === null ? fallback : result;
}

function cleanHtmlTags(val) {
  if (typeof val === "string") {
    return val.replace(/<\/?p>/gi, " ").trim();
  }
  if (Array.isArray(val)) {
    return val.map(item => cleanHtmlTags(item));
  }
  if (val !== null && typeof val === "object") {
    const cleaned = {};
    for (let k in val) cleaned[k] = cleanHtmlTags(val[k]);
    return cleaned;
  }
  return val;
}

function getInitialValue(val) {
  if (val === null || val === undefined) return "";
  return cleanHtmlTags(val);
}

function setDeep(obj, path, newValue) {
  const root = Array.isArray(obj) ? [...obj] : { ...obj };
  let current = root;
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i];
    current[key] = Array.isArray(current[key]) ? [...current[key]] : { ...current[key] };
    current = current[key];
  }
  current[path[path.length - 1]] = newValue;
  return root;
}

// ============================================================
// FIX 2 – Deprecated values warning badge
// ============================================================
function DeprecatedWarningBadge({ value, deprecatedValues }) {
  if (!deprecatedValues || !deprecatedValues.length) return null;
  const isDeprecated = deprecatedValues.some(
    (d) => String(d).toLowerCase() === String(value || "").toLowerCase()
  );
  if (!isDeprecated) return null;
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 4,
      marginTop: 3,
      fontSize: 11,
      color: "#b45309",
      background: "#fffbeb",
      border: "1px solid #fcd34d",
      borderRadius: 4,
      padding: "2px 6px"
    }}>
      <span>⚠️</span>
      <span>
        This value is <strong>deprecated</strong> by Amazon. Please select an updated value.
      </span>
    </div>
  );
}

// ============================================================
// FIX 1 + FIX 6 – Pattern / URL inline validation hint
// ============================================================
function PatternValidationHint({ value, rules }) {
  if (!value || typeof value !== "string" || value.trim() === "") return null;
  const pattern = rules && rules.pattern;
  const format = rules && rules.format;

  if (pattern) {
    try {
      if (!new RegExp(pattern).test(value)) {
        return (
          <small style={{ color: "#dc2626", fontSize: 11, display: "block", marginTop: 2 }}>
            {format === "uri"
              ? "Invalid URL – must start with https:// or s3://"
              : `Invalid format (pattern: ${pattern})`}
          </small>
        );
      }
    } catch (e) { /* invalid regex – skip */ }
  } else if (format === "uri") {
    try {
      const u = new URL(value);
      if (!u.protocol || !u.host) throw new Error();
    } catch {
      return (
        <small style={{ color: "#dc2626", fontSize: 11, display: "block", marginTop: 2 }}>
          Invalid URL format
        </small>
      );
    }
  }
  return null;
}

const AutoResizeTextarea = ({ value, onChange, readOnly, style, placeholder }) => {
  const textareaRef = useRef(null);
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const extraRowHeight = 18;
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight + extraRowHeight}px`;
    }
  }, [value]);
  return (
    <textarea
      ref={textareaRef}
      style={{ ...style, resize: "none", overflow: "hidden", boxSizing: "border-box", fontFamily: "inherit", lineHeight: "1.4" }}
      value={value}
      onChange={onChange}
      readOnly={readOnly}
      placeholder={placeholder}
      rows={1}
    />
  );
};

function AmazonProductListingEditableField({ field = {}, onFieldChange, changedValue, isUpdated }) {
  // ============================================================
  // FIX 4 – default_value prefill when normalized_value is empty
  // ============================================================
  var rawValue = isUpdated ? changedValue : get(field, "normalized_value", null);
  if (rawValue === null || rawValue === undefined || rawValue === "") {
    const defaultVal = get(field, "schema.default_value", null);
    if (defaultVal !== null && defaultVal !== undefined) rawValue = defaultVal;
  }
  const _isNestedObject = get(field, "render.component", "") === "NESTED_OBJECT";
  if ((rawValue === null || rawValue === undefined || rawValue === "") && _isNestedObject) {
    const itemProps = get(field, "schema.nested_schema.items.properties", null);
    if (itemProps) {
      const emptyObj = {};
      Object.keys(itemProps).forEach(k => { emptyObj[k] = ""; });
      rawValue = [emptyObj];
    }
  }
  var initial = getInitialValue(rawValue);

  const [showSuggestions, setShowSuggestions] = useState(false);
  var [value, setValue] = useState(initial);

  var options = get(field, "schema.allowed_values", []) || [];
  var allowCustomValues = get(field, "schema.rules.allow_custom_values", false);
  const component = get(field, "render.component", "TEXT_INPUT");
  const isDropdown = component === "SELECT";
  const isToggle = component === "TOGGLE";
  const isNestedObject = component === "NESTED_OBJECT";
  var rules = get(field, "schema.rules", {}) || {};
  const isArrayField = rules.field_type === "array";
  const isMultiValueField =
    isArrayField &&
    (
      (rules.maxItems && rules.maxItems > 1) ||
      (rules.maxUniqueItems && rules.maxUniqueItems > 1)
    );
  // Type A: single value suggestive (maxUniqueItems=1, allow_custom_values=true)
  var isSuggestiveField = options.length > 0 && allowCustomValues && !isMultiValueField;
  // Type B: multi-value suggestive (maxUniqueItems>1, allow_custom_values=true) 
  var isSuggestiveMultiField = options.length > 0 && allowCustomValues && isMultiValueField;
  var isEditable = get(field, "schema.editable", true);
  var examples = get(field, "schema.examples", []) || [];
  var fieldType = rules.type;
  var actualFieldType = fieldType;
  var maxLength = actualFieldType === "string" ? rules.maxLength : null;
  var maxUtf8ByteLength = rules.maxUtf8ByteLength;
  var minLength = rules.minLength;
  var minimum = rules.minimum;
  var maximum = rules.maximum;
  var exclusiveMinimum = rules.exclusiveMinimum;
  var exclusiveMaximum = rules.exclusiveMaximum;
  var multipleOf = rules.multipleOf;
  var decimalPlaces = null;
  if (multipleOf) {
    const multipleString = Number(multipleOf).toFixed(10);
    const decimalPart = multipleString.split(".")[1];
    if (decimalPart) {
      decimalPlaces = decimalPart.replace(/0+$/, "").length;
    }
  }
  var isNumberField = (actualFieldType === "number" || actualFieldType === "integer") && !Array.isArray(value) && typeof value !== "object";
  var isDateField = rules.format === "date" || rules.format === "date-time" || get(field, "ui.format", "") === "date" || get(field, "ui.format", "") === "date-time";
  var placeholder = actualFieldType === "boolean" ? "Select" : (examples.length > 0 ? `e.g. ${examples[0]}` : "");
  var currentLength = typeof value === "string" ? value.length : 0;
  var currentUtf8Length = typeof value === "string" ? new TextEncoder().encode(value).length : 0;
  var isTooLong = (maxLength && currentLength > maxLength) || (maxUtf8ByteLength && currentUtf8Length > maxUtf8ByteLength);
  var numericValue = Number(value);
  var isNumberInvalid = isNumberField && value !== "" && (
    (minimum !== undefined && numericValue < minimum) ||
    (maximum !== undefined && numericValue > maximum) ||
    (exclusiveMinimum !== undefined && numericValue <= exclusiveMinimum) ||
    (exclusiveMaximum !== undefined && numericValue >= exclusiveMaximum)
  );
  var isDateInvalid = false;
  if (isDateField && value) {
    const parsedDate = new Date(value);
    isDateInvalid = isNaN(parsedDate.getTime());
  }
  var isTooShort = !!minLength && currentLength < minLength && currentLength > 0;

  // ============================================================
  // FIX 3 – const field detection
  // ============================================================
  var constValue = get(field, "schema.rules.const", undefined);
  var isConstField = constValue !== undefined;

  var inputStyle = {
    width: "100%", fontSize: 12, padding: "4px 8px", borderRadius: 6,
    border: isTooLong || isTooShort || isNumberInvalid || isDateInvalid ? "1px solid red" : "1px solid #ccc",
    background: "var(--color-background-primary, #ffffff)", color: "#333", cursor: "text"
  };
  var plainTextStyle = { width: "100%", fontSize: 12, padding: "4px 0px", color: "#555", wordBreak: "break-word" };

  const getDisplayText = (val) => {
    if (val === null || val === undefined) return "—";
    const strVal = String(val).trim();
    return strVal === "" ? "—" : strVal;
  };

  const handleStandardChange = (newVal) => {
    if (!isEditable) return;
    setValue(newVal);
    if (onFieldChange) onFieldChange({ field: field.field, value: newVal });
  };

  const handleDeepChange = (path, newVal) => {
    if (!isEditable) return;
    const updatedObject = setDeep(value, path, newVal);
    setValue(updatedObject);
    if (onFieldChange) onFieldChange({ field: field.field, value: updatedObject });
  };

  const triggerFieldChange = (updatedValue) => {
    setValue(updatedValue);
    if (onFieldChange) onFieldChange({ field: field.field, value: updatedValue });
  };

  useEffect(() => {
    let newRaw = isUpdated ? changedValue : getInitialValue(get(field, "normalized_value", null));
    if (newRaw === null || newRaw === undefined || newRaw === "") {
      const defaultVal = get(field, "schema.default_value", null);
      if (defaultVal !== null && defaultVal !== undefined) newRaw = defaultVal;
    }
    if ((newRaw === null || newRaw === undefined || newRaw === "") && isNestedObject) {
      const itemProps = get(field, "schema.nested_schema.items.properties", null);
      if (itemProps) {
        const emptyObj = {};
        Object.keys(itemProps).forEach(k => { emptyObj[k] = ""; });
        newRaw = [emptyObj];
      }
    }
    setValue(newRaw);
  }, [field, changedValue, isUpdated]);

  // ============================================================
  // FIX 3 – const field → locked display (before other renders)
  // ============================================================
  if (isConstField) {
    return (
      <div style={{ width: "100%", display: "flex", alignItems: "center", gap: 6 }}>
        <div style={{ ...plainTextStyle, flex: 1 }}>{String(constValue)}</div>
        <span style={{
          fontSize: 10, padding: "1px 6px", borderRadius: 4,
          background: "#f3f4f6", color: "#6b7280", border: "1px solid #e5e7eb", whiteSpace: "nowrap"
        }}>
          Fixed
        </span>
      </div>
    );
  }

  if (isSuggestiveField) {
    const filteredOptions = options.filter(opt =>
      String(opt).toLowerCase().includes(String(value || "").toLowerCase())
    );
    return (
      <div style={{ width: "100%", position: "relative" }}>
        <input
          style={{ ...inputStyle, background: "#fff", color: "#111" }}
          value={value !== null && value !== undefined ? String(value) : ""}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => { setTimeout(() => setShowSuggestions(false), 150); }}
          onChange={(e) => { handleStandardChange(e.target.value); setShowSuggestions(true); }}
          placeholder={placeholder}
          autoComplete="off"
        />
        {/* FIX 1 – pattern validation */}
        <PatternValidationHint value={value} rules={rules} />
        {/* FIX 2 – deprecated warning */}
        <DeprecatedWarningBadge value={value} deprecatedValues={get(field, "schema.deprecated_values", [])} />
        {showSuggestions && filteredOptions.length > 0 && (
          <div style={{
            position: "absolute", top: "100%", left: 0, right: 0, background: "#fff",
            border: "1px solid #d5d9d9", borderRadius: "8px", marginTop: "2px",
            maxHeight: "220px", overflowY: "auto", zIndex: 9999, boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
          }}>
            {filteredOptions.slice(0, 50).map((opt) => (
              <div key={String(opt)}
                onMouseDown={() => { handleStandardChange(String(opt)); setShowSuggestions(false); }}
                style={{ padding: "10px 12px", cursor: "pointer", fontSize: "12px", background: "#fff", color: "#111" }}
                onMouseEnter={(e) => { e.target.style.background = "#f3f3f3"; }}
                onMouseLeave={(e) => { e.target.style.background = "#fff"; }}
              >
                {String(opt)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (isToggle) {
    const toggleValue = value === true || value === "true" || value === "Yes" || value === "yes";
    if (!isEditable) {
      return <div style={plainTextStyle}>{toggleValue ? "Yes" : "No"}</div>;
    }
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <label style={{ position: "relative", display: "inline-block", width: "38px", height: "22px", marginBottom: 0, cursor: "pointer" }}>
          <input type="checkbox" checked={toggleValue} onChange={(e) => handleStandardChange(e.target.checked)}
            style={{ opacity: 0, width: 0, height: 0 }} />
          <span style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: toggleValue ? "#0073bb" : "#c4c4c4", borderRadius: "34px", transition: "0.2s" }}>
            <span style={{ position: "absolute", height: "18px", width: "18px", left: toggleValue ? "18px" : "2px", bottom: "2px", backgroundColor: "white", borderRadius: "50%", transition: "0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
          </span>
        </label>
        <span style={{ fontSize: "12px", color: "#111827", minWidth: "24px" }}>{toggleValue ? "Yes" : "No"}</span>
      </div>
    );
  }
  if (isSuggestiveMultiField) {
    const currentValues = Array.isArray(value) ? value : (value ? [value] : [""]);
    const maxAllowed = rules.maxUniqueItems || rules.maxItems || 99;
    return (
      <div style={{ width: "100%" }}>
        {currentValues.map((item, index) => {
          const filtered = options.filter(opt =>
            String(opt).toLowerCase().includes(String(item || "").toLowerCase())
          );
          return (
            <div key={index} style={{ position: "relative", marginBottom: "6px", display: "flex", gap: "6px", alignItems: "center" }}>
              <div style={{ flex: 1, position: "relative" }}>
                <input
                  style={{ ...inputStyle, background: "#fff", color: "#111" }}
                  value={item !== null && item !== undefined ? String(item) : ""}
                  onChange={(e) => {
                    const updated = [...currentValues];
                    updated[index] = e.target.value;
                    triggerFieldChange(updated);
                  }}
                  onFocus={(e) => { const list = e.target.parentNode.querySelector(".sug-list"); if (list) list.style.display = "block"; }}
                  onBlur={(e) => { const parent = e.target.parentNode; setTimeout(() => { const list = parent.querySelector(".sug-list"); if (list) list.style.display = "none"; }, 150); }}
                  placeholder={placeholder || `e.g. ${options[0] || ""}`}
                  autoComplete="off"
                />
                {filtered.length > 0 && (
                  <div className="sug-list" style={{
                    display: "none", position: "absolute", top: "100%", left: 0, right: 0,
                    background: "#fff", border: "1px solid #d5d9d9", borderRadius: "8px",
                    marginTop: "2px", maxHeight: "180px", overflowY: "auto", zIndex: 9999,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
                  }}>
                    {filtered.slice(0, 50).map((opt) => (
                      <div key={String(opt)}
                        onMouseDown={() => {
                          const updated = [...currentValues];
                          updated[index] = String(opt);
                          triggerFieldChange(updated);
                        }}
                        style={{ padding: "8px 12px", cursor: "pointer", fontSize: "12px" }}
                        onMouseEnter={(e) => { e.target.style.background = "#f3f3f3"; }}
                        onMouseLeave={(e) => { e.target.style.background = "#fff"; }}
                      >
                        {String(opt)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {currentValues.length > 1 && (
                <span style={{ color: "#dc2626", cursor: "pointer", fontSize: "18px", lineHeight: 1 }}
                  onClick={() => triggerFieldChange(currentValues.filter((_, i) => i !== index))}>
                  ×
                </span>
              )}
            </div>
          );
        })}
        {currentValues.length < maxAllowed && (
          <span style={{ color: "#007185", cursor: "pointer", fontSize: "12px" }}
            onClick={() => triggerFieldChange([...currentValues, ""])}>
            + Add More
          </span>
        )}
        {(() => {
          const filled = currentValues.filter(v => v !== "" && v !== null && v !== undefined);
          const hasDup = new Set(filled.map(v => String(v).trim().toLowerCase())).size !== filled.length;
          return hasDup ? (
            <div style={{ color: "#dc2626", fontSize: "11px", marginTop: "4px" }}>
              Duplicate values are not allowed
            </div>
          ) : null;
        })()}
      </div>
    );
  }
  if (isDropdown && isMultiValueField) {

    const currentValues = Array.isArray(value)
      ? value
      : value
        ? [value]
        : [""];
    const normalizedValues = currentValues
      .filter(v => v !== null && v !== undefined && v !== "")
      .map(v => String(v).trim().toLowerCase());

    const hasDuplicateValues =
      new Set(normalizedValues).size !== normalizedValues.length;
    return (
      <div style={{ width: "100%" }}>
        {currentValues.map((item, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: "6px",
              alignItems: "center"
            }}
          >
            <select
              style={inputStyle}
              value={item || ""}
              onChange={(e) => {
                const updated = [...currentValues];
                updated[index] = e.target.value;

                setValue(updated);

                if (onFieldChange) {
                  onFieldChange({
                    field: field.field,
                    value: updated
                  });
                }
              }}
            >
              <option value="">
                {placeholder || "Select"}
              </option>

              {options.map((opt, optIndex) => {
                const optionLabels =
                  get(field, "schema.allowed_value_labels", []);

                const label =
                  optionLabels[optIndex] || String(opt);

                return (
                  <option
                    key={String(opt)}
                    value={String(opt)}
                  >
                    {label}
                  </option>
                );
              })}
            </select>

            {currentValues.length > 1 && (
              <button
                type="button"
                onClick={() => {
                  const updated =
                    currentValues.filter((_, i) => i !== index);

                  setValue(updated);

                  if (onFieldChange) {
                    onFieldChange({
                      field: field.field,
                      value: updated
                    });
                  }
                }}
              >
                Remove
              </button>
            )}
          </div>
        ))}

        <span
          style={{
            color: "#007185",
            cursor: "pointer",
            fontSize: "12px"
          }}
          onClick={() => {

            const updated = [...currentValues, ""];

            setValue(updated);

            if (onFieldChange) {
              onFieldChange({
                field: field.field,
                value: updated
              });
            }
          }}
        >
          Add More
        </span>
        {hasDuplicateValues && (
          <div
            style={{
              color: "#dc2626",
              fontSize: "11px",
              marginTop: "4px"
            }}
          >
            Duplicate values are not allowed
          </div>
        )}
      </div>
    );
  }
  if (isDropdown && !isMultiValueField) {
    let selectedValue = "";
    if (value !== null && value !== undefined) {
      selectedValue = Array.isArray(value) ? (value.length > 0 ? String(value[0]) : "") : String(value);
    }
    if (!isEditable) {
      return <div style={plainTextStyle}>{getDisplayText(selectedValue)}</div>;
    }
    return (
      <div>
        <select style={inputStyle} value={selectedValue}
          onChange={(e) => {
            if (!isEditable) return;
            const originalIsArray = Array.isArray(get(field, "normalized_value", null));
            const newVal = originalIsArray ? [e.target.value] : e.target.value;
            setValue(newVal);
            if (onFieldChange) onFieldChange({ field: field.field, value: newVal });
          }}
        >
          <option value="">{placeholder || "Select"}</option>
          {options.map((opt, index) => {
            const optionLabels = get(field, "schema.allowed_value_labels", []);
            const label = optionLabels[index] ? optionLabels[index] : String(opt);
            return <option key={String(opt)} value={String(opt)}>{label}</option>;
          })}
        </select>
        {/* FIX 2 – deprecated warning on dropdown */}
        <DeprecatedWarningBadge value={selectedValue} deprecatedValues={get(field, "schema.deprecated_values", [])} />
      </div>
    );
  }

  const isEnumField = (schema = {}) => Array.isArray(schema.enum);
  const isValueUnitField = (schema = {}) => schema && schema.properties && schema.properties.value && schema.properties.unit;
  const isPrimitiveField = (schema = {}) => ["string", "number", "integer", "boolean"].includes(schema.type);
  const isDateSchema = (schema = {}) => {
    if (schema.format === "date" || schema.format === "date-time") return true;
    if (schema.oneOf && Array.isArray(schema.oneOf)) {
      return schema.oneOf.some(item => item.format === "date" || item.format === "date-time");
    }
    return false;
  };

  const renderDeep = (data, schema = {}, path = [], isObjProp = false) => {
    if (data === null || data === undefined) {
      if (!isEditable) {
        return <div style={{ ...plainTextStyle, width: isObjProp ? "60%" : "100%" }}>—</div>;
      }
      if (isNumberField) {
        return (
          <div style={{ width: isObjProp ? "60%" : "100%" }}>
            <AutoResizeTextarea style={{ ...inputStyle, width: "100%", marginBottom: 0 }} value=""
              onChange={(e) => handleDeepChange(path, e.target.value)} placeholder={placeholder} />
            <div className="mt-1"><small className="text-muted">
              Allowed range: {minimum !== undefined && maximum !== undefined && <>{minimum} - {maximum}</>}
              {minimum !== undefined && maximum === undefined && <>Min: {minimum}</>}
              {minimum === undefined && maximum !== undefined && <>Max: {maximum}</>}
            </small></div>
          </div>
        );
      }
      return null;
    }

    if (typeof data !== "object") {
      const localFieldType = schema.type;
      if (!isEditable) {
        return <div style={{ ...plainTextStyle, width: isObjProp ? "60%" : "100%" }}>{getDisplayText(data)}</div>;
      }
      const textValue = data !== null && data !== undefined ? String(data) : "";
      const localIsNumberField = (localFieldType === "number" || localFieldType === "integer") && !isNaN(Number(textValue)) && textValue !== "";
      const localIsDateField = isDateSchema(schema);

      const localMaxLength = schema.maxLength !== undefined ? schema.maxLength : maxLength;
      const localMinLength = schema.minLength !== undefined ? schema.minLength : minLength;
      const localMaxUtf8 = schema.maxUtf8ByteLength !== undefined ? schema.maxUtf8ByteLength : maxUtf8ByteLength;

      const localPlaceholder = schema.examples && schema.examples.length > 0
        ? `e.g. ${schema.examples[0]}`
        : placeholder;
      const currentLength = textValue.length;
      const currentUtf8Length = new TextEncoder().encode(textValue).length;
      const isTooLong = (localMaxLength && currentLength > localMaxLength) || (localMaxUtf8 && currentUtf8Length > localMaxUtf8);
      const numericValue = Number(textValue);
      // ============================================================
      // FIX 5 – local min/max from schema (for nested value fields like list_price, non_lithium_battery_energy_content)
      // ============================================================
      const localMinimum = schema.minimum !== undefined ? schema.minimum : minimum;
      const localMaximum = schema.maximum !== undefined ? schema.maximum : maximum;
      const localExclusiveMin = schema.exclusiveMinimum !== undefined ? schema.exclusiveMinimum : exclusiveMinimum;
      const localExclusiveMax = schema.exclusiveMaximum !== undefined ? schema.exclusiveMaximum : exclusiveMaximum;
      const isNumberInvalid = localIsNumberField && textValue !== "" && (
        (localMinimum !== undefined && numericValue < localMinimum) || (localMaximum !== undefined && numericValue > localMaximum) ||
        (localExclusiveMin !== undefined && numericValue <= localExclusiveMin) || (localExclusiveMax !== undefined && numericValue >= localExclusiveMax)
      );
      // ============================================================
      // FIX 5 – compute localDecimalPlaces from schema.multipleOf (nested fields)
      // Falls back to outer decimalPlaces if schema has no multipleOf
      // ============================================================
      let localDecimalPlaces = decimalPlaces; // outer scope fallback
      if (schema.multipleOf !== undefined) {
        const ms = Number(schema.multipleOf).toFixed(10);
        const dp = ms.split(".")[1];
        localDecimalPlaces = dp ? dp.replace(/0+$/, "").length : 0;
      }
      return (
        <div style={{ width: isObjProp ? "60%" : "100%" }}>
          {localIsDateField ? (
            <input type="date" style={{ ...inputStyle, width: "100%", marginBottom: 0 }}
              value={textValue ? textValue.split("T")[0] : ""}
              onChange={(e) => handleDeepChange(path, e.target.value)} />
          ) : (
            options.length > 0 ? (
              <>
                <input list={`suggestions-${field.field}`} style={{ ...inputStyle, width: "100%", marginBottom: 0 }}
                  value={textValue}
                  onFocus={(e) => { const input = e.target; const current = input.value; input.value = ""; setTimeout(() => { input.value = current; }, 0); }}
                  onChange={(e) => { handleDeepChange(path, e.target.value); }}
                  placeholder={localPlaceholder} autoComplete="off" />
                <datalist id={`suggestions-${field.field}`}>
                  {options.map((opt) => <option key={String(opt)} value={String(opt)} />)}
                </datalist>
              </>
            ) : (
              <AutoResizeTextarea
                style={{ ...inputStyle, width: "100%", marginBottom: 0, border: isTooLong || isTooShort || isNumberInvalid ? "1px solid red" : "1px solid #ccc" }}
                value={textValue}
                onChange={(e) => {
                  let newValue = e.target.value;
                  if (localIsNumberField) {
                    // FIX 5: use localDecimalPlaces (from schema.multipleOf) not outer decimalPlaces
                    const numberRegex = localFieldType === "integer" ? /^\d*$/ : (localDecimalPlaces === null ? /^\d*\.?\d*$/ : new RegExp(`^\\d*\\.?\\d{0,${localDecimalPlaces}}$`));
                    if (newValue !== "" && !numberRegex.test(newValue)) return;
                  }
                  handleDeepChange(path, newValue);
                }}
                placeholder={localPlaceholder} />
            )
          )}
          {/* FIX 1 – pattern/URL validation in renderDeep */}
          <PatternValidationHint value={textValue} rules={schema} />
          {!localIsNumberField && (localMaxLength || localMaxUtf8 || localMinLength) && (
            <div className="d-flex justify-content-between">
              <div>
                {isTooLong && <small className="text-danger">
                  {localMaxUtf8 ? `Maximum ${localMaxUtf8} UTF8 bytes allowed` : `Maximum ${localMaxLength} characters allowed`}
                </small>}
                {isTooShort && <small className="text-danger">Minimum {localMinLength} characters required</small>}
              </div>
              <small className={isTooLong ? "text-danger" : "text-muted"}>
                {currentLength}{localMaxLength ? ` / ${localMaxLength}` : (localMaxUtf8 ? ` / ${localMaxUtf8} bytes` : "")}
              </small>
            </div>
          )}
          {/* FIX 5 range hint for nested number fields (list_price.value, non_lithium_battery_energy_content.value etc.) */}
          {localIsNumberField && (localMinimum !== undefined || localMaximum !== undefined) && !isNumberInvalid && (
            <div className="d-flex justify-content-end mt-1">
              <small className="text-muted">
                {localMinimum !== undefined && localMaximum !== undefined && <>{localMinimum} {"–"} {localMaximum}</>}
                {localMinimum !== undefined && localMaximum === undefined && <>Min: {localMinimum}</>}
                {localMinimum === undefined && localMaximum !== undefined && <>Max: {localMaximum}</>}
              </small>
            </div>
          )}
          {isNumberInvalid && (
            <div className="d-flex justify-content-end">
              {localExclusiveMin !== undefined && numericValue <= localExclusiveMin && <small className="text-danger">Value must be greater than {localExclusiveMin}</small>}
              {localExclusiveMax !== undefined && numericValue >= localExclusiveMax && <small className="text-danger">Value must be less than {localExclusiveMax}</small>}
              {localMinimum !== undefined && localMaximum !== undefined && localExclusiveMin === undefined && localExclusiveMax === undefined && <small className="text-danger">Value must be between {localMinimum} {"–"} {localMaximum}</small>}
              {localMinimum !== undefined && localMaximum === undefined && localExclusiveMin === undefined && <small className="text-danger">Value must be at least {localMinimum}</small>}
              {localMinimum === undefined && localMaximum !== undefined && localExclusiveMax === undefined && <small className="text-danger">Maximum value is {localMaximum}</small>}
            </div>
          )}
        </div>
      );
    }

    if (Array.isArray(data)) {
      const normalizedValues = data.filter(item => item !== null && item !== undefined && item !== "").map(item => JSON.stringify(item));
      const hasDuplicateValues = new Set(normalizedValues).size !== normalizedValues.length;
      const maxAllowedItems = schema.maxItems !== undefined ? schema.maxItems : schema.maxUniqueItems;
      const minAllowedItems = schema.minItems !== undefined ? schema.minItems : 1;
      return (
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
          {data.map((item, index) => {
            const isPrimitive = typeof item !== "object" || item === null;
            const isCompactObject = item && typeof item === "object" && Object.keys(item).length <= 3;
            return (
              <div key={index} style={{ marginBottom: (isPrimitive || isCompactObject) ? "4px" : (index < data.length - 1 ? "8px" : "0") }}>
                {renderDeep(item, schema.items ? schema.items : schema, [...path, index])}
              </div>
            );
          })}
          {isEditable && path.length <= 1 && (
            <div style={{ display: "flex", gap: "12px", fontSize: "12px" }}>
              {maxAllowedItems > 1 && data.length < maxAllowedItems && (
                <span style={{ color: "#007185", cursor: "pointer" }}
                  onClick={() => { const updated = [...data, ""]; setValue(updated); if (onFieldChange) onFieldChange({ field: field.field, value: updated }); }}>
                  Add More
                </span>
              )}
              {maxAllowedItems > 1 &&
                data.length > minAllowedItems && (
                  <span style={{ color: "#007185", cursor: "pointer" }}
                    onClick={() => { const updated = data.slice(0, -1); setValue(updated); if (onFieldChange) onFieldChange({ field: field.field, value: updated }); }}>
                    Remove Last
                  </span>
                )}
            </div>
          )}
          {hasDuplicateValues && <div className="text-danger" style={{ fontSize: "11px", marginTop: "4px" }}>Duplicate values are not allowed</div>}
        </div>
      );
    }

    if (typeof data === "object" && !Array.isArray(data)) {
      const objectProperties = schema.properties || get(schema, "items.properties", {});
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {Object.entries(objectProperties).map(([key, propertySchema]) => {
            if (key === "marketplace_id" || key === "language_tag") return null;
            const fieldValue = data && data[key] !== undefined ? data[key] : undefined;
            const isHidden = propertySchema.hidden === true;
            if (isHidden) return null;
            const currentPath = [...path, key];
            const isEditableField = propertySchema.editable !== false;
            if (!isEditableField) return <div style={plainTextStyle}>{String(fieldValue || "-")}</div>;
            const isBooleanEnumField = propertySchema.type === "boolean" || (Array.isArray(propertySchema.enum) && propertySchema.enum.every(item => typeof item === "boolean"));
            if (isBooleanEnumField) {
              const toggleValue = fieldValue === true;
              return (
                <div key={key}>
                  <div className="mb-1">{propertySchema.title || key}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <label style={{ position: "relative", display: "inline-block", width: "38px", height: "22px", marginBottom: 0, cursor: "pointer" }}>
                      <input type="checkbox" checked={toggleValue} onChange={(e) => { const updated = setDeep(value, currentPath, e.target.checked); triggerFieldChange(updated); }} style={{ opacity: 0, width: 0, height: 0 }} />
                      <span style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: toggleValue ? "#0073bb" : "#c4c4c4", borderRadius: "34px", transition: "0.2s" }}>
                        <span style={{ position: "absolute", height: "18px", width: "18px", left: toggleValue ? "18px" : "2px", bottom: "2px", backgroundColor: "white", borderRadius: "50%", transition: "0.2s" }} />
                      </span>
                    </label>
                    <span style={{ fontSize: "12px" }}>{toggleValue ? "Enabled" : "Disabled"}</span>
                  </div>
                </div>
              );
            }
            if (isEnumField(propertySchema)) {
              const enumOptions = propertySchema.enum || [];
              const finalOptions = (fieldValue && !enumOptions.includes(fieldValue)) ? [fieldValue, ...enumOptions] : enumOptions;
              return (
                <div key={key}>
                  <div className="mb-1">{propertySchema.title || key}</div>
                  <select style={inputStyle} value={fieldValue !== undefined && fieldValue !== null ? String(fieldValue) : ""}
                    onChange={(e) => {
                      let selectedValue = e.target.value;
                      if (propertySchema.type === "boolean") {
                        if (selectedValue === "true") selectedValue = true;
                        else if (selectedValue === "false") selectedValue = false;
                      }
                      triggerFieldChange(setDeep(value, currentPath, selectedValue));
                    }}>
                    <option value="">Select</option>
                    {finalOptions.map((opt, index) => {
                      const optionLabel = propertySchema.enumNames && propertySchema.enumNames[index] ? propertySchema.enumNames[index] : String(opt);
                      return <option key={String(opt)} value={propertySchema.type === "boolean" ? String(opt) : opt}>{optionLabel}</option>;
                    })}
                  </select>
                </div>
              );
            }
            if (isValueUnitField(propertySchema)) {
              const valExamples = get(propertySchema, "properties.value.examples", []);
              const valPlaceholder = valExamples.length > 0 ? `e.g. ${valExamples[0]}` : "";
              return (
                <div key={key}>
                  <div className="mb-1">{propertySchema.title || key}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                    <input type={propertySchema.properties.value.type === "number" || propertySchema.properties.value.type === "integer" ? "number" : "text"} step="any" style={inputStyle}
                      placeholder={valPlaceholder}
                      value={fieldValue && fieldValue.value !== undefined ? fieldValue.value : ""}
                      onChange={(e) => triggerFieldChange(setDeep(value, [...currentPath, "value"], e.target.value))} />
                    <select style={inputStyle} value={fieldValue && fieldValue.unit !== undefined ? fieldValue.unit : ""}
                      onChange={(e) => triggerFieldChange(setDeep(value, [...currentPath, "unit"], e.target.value))}>
                      <option value="">Select Unit</option>
                      {(propertySchema.properties.unit.enum || []).map((unit, index) => {
                        const unitLabels = propertySchema.properties.unit.enumNames || [];
                        return <option key={unit} value={unit}>{unitLabels[index] ? unitLabels[index] : unit}</option>;
                      })}
                    </select>
                  </div>
                </div>
              );
            }
            if (isPrimitiveField(propertySchema)) {
              const isDateField = isDateSchema(propertySchema);
              return (
                <div key={key}>
                  <div className="mb-1">{propertySchema.title || key}</div>
                  {isDateField ? (
                    <input type="date" style={inputStyle} value={fieldValue ? String(fieldValue).split("T")[0] : ""}
                      onChange={(e) => triggerFieldChange(setDeep(value, currentPath, e.target.value))} />
                  ) : (
                    renderDeep(fieldValue, propertySchema, currentPath)
                  )}
                </div>
              );
            }
            return (
              <div key={key}>
                {(!Array.isArray(fieldValue) && typeof fieldValue !== "object") && <div className="mb-1 fw-bold">{propertySchema.title || key}</div>}
                {renderDeep(fieldValue, propertySchema, currentPath)}
              </div>
            );
          })}
        </div>
      );
    }
  };

  if (Array.isArray(value) || (value !== null && typeof value === "object")) {
    const nestedSchema = get(field, "schema.nested_schema", {});
    const schemaToUse = nestedSchema;
    return renderDeep(value, schemaToUse);
  }

  if (!isEditable) {
    return <div style={plainTextStyle}>{getDisplayText(value)}</div>;
  }

  return (
    <div style={{ width: "100%" }}>
      {isDateField ? (
        <input type="date" style={inputStyle} value={value ? String(value).split("T")[0] : ""}
          onChange={(e) => handleStandardChange(e.target.value)} />
      ) : (
        <AutoResizeTextarea style={inputStyle}
          value={value !== null && value !== undefined ? String(value) : ""}
          onChange={(e) => {
            let newValue = e.target.value;
            if (isNumberField) {
              const numberRegex = actualFieldType === "integer" ? /^\d*$/ : (decimalPlaces === null ? /^\d*\.?\d*$/ : new RegExp(`^\\d*\\.?\\d{0,${decimalPlaces}}$`));
              if (newValue !== "" && !numberRegex.test(newValue)) return;
            }
            handleStandardChange(newValue);
          }}
          placeholder={placeholder} />
      )}
      {/* FIX 1 – pattern/URL inline validation */}
      <PatternValidationHint value={value} rules={rules} />
      {!isNumberField && (maxLength || maxUtf8ByteLength || minLength) && (
        <div className="d-flex justify-content-between">
          <div>{isTooLong && <small className="text-danger">{maxUtf8ByteLength ? `Maximum ${maxUtf8ByteLength} UTF8 bytes allowed` : `Maximum ${maxLength} characters allowed`}</small>}</div>
          <small className={isTooLong ? "text-danger" : "text-muted"}>{currentLength}{maxLength ? ` / ${maxLength}` : (maxUtf8ByteLength ? ` / ${maxUtf8ByteLength} bytes` : "")}</small>
        </div>
      )}
      {isNumberField && minimum !== undefined && (actualFieldType === "number" || actualFieldType === "integer") && (
        <div className="d-flex justify-content-end mt-1">
          <small className="text-muted">
            {minimum !== undefined && maximum !== undefined && <>{minimum} - {maximum}</>}
            {minimum !== undefined && maximum === undefined && <>Min: {minimum}</>}
            {minimum === undefined && maximum !== undefined && <>Max: {maximum}</>}
          </small>
        </div>
      )}
      {isNumberInvalid && (
        <div className="d-flex justify-content-end">
          {exclusiveMinimum !== undefined && numericValue <= exclusiveMinimum && <small className="text-danger">Value must be greater than {exclusiveMinimum}</small>}
          {exclusiveMaximum !== undefined && numericValue >= exclusiveMaximum && <small className="text-danger">Value must be less than {exclusiveMaximum}</small>}
          {minimum !== undefined && maximum !== undefined && exclusiveMinimum === undefined && exclusiveMaximum === undefined && <small className="text-danger">Value must be between {minimum} - {maximum}</small>}
          {minimum !== undefined && maximum === undefined && exclusiveMinimum === undefined && <small className="text-danger">Value must be at least {minimum}</small>}
          {minimum === undefined && maximum !== undefined && exclusiveMaximum === undefined && <small className="text-danger">Maximum value is {maximum}</small>}
        </div>
      )}
      {isDateInvalid && (
        <div className="d-flex justify-content-end mt-1">
          <small className="text-danger">Invalid date format (YYYY-MM-DD)</small>
        </div>
      )}
    </div>
  );
}

export default AmazonProductListingEditableField;